import { createBrowserRouter } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import MainLayout from '@layouts/MainLayout';
import BlogLayout from '@layouts/BlogLayout';
import AdminLayout from '@layouts/AdminLayout';
import PageLoader from '@components/common/PageLoader';
import ProtectedAdminRoute from '@components/admin/ProtectedAdminRoute';
import { AdminAuthProvider } from '@/context/AdminAuthContext';
import { ToastProvider } from '@/context/ToastContext';
import { Navigate } from 'react-router-dom';

// ── Lazy-loaded public page components ────────────────────────────────────
const Home = lazy(() => import('@pages/Home'));
const Products = lazy(() => import('@pages/Products'));
const Services = lazy(() => import('@pages/Services'));
const UserGuide = lazy(() => import('@pages/UserGuide'));
const Features = lazy(() => import('@pages/Features'));
const Solutions = lazy(() => import('@pages/Solutions'));
const Blog = lazy(() => import('@pages/Blog'));
const BlogPost = lazy(() => import('@pages/Blog/BlogPost'));
const NotFound = lazy(() => import('@pages/NotFound'));

// ── Lazy-loaded admin page components ─────────────────────────────────────
const AdminLogin   = lazy(() => import('@pages/Admin/Login'));
const AdminSignup  = lazy(() => import('@pages/Admin/Signup'));
const Dashboard    = lazy(() => import('@pages/Admin/Dashboard'));
const UploadModel  = lazy(() => import('@pages/Admin/Models/UploadModel'));
const ModelsList   = lazy(() => import('@pages/Admin/Models/ModelsList'));
const Menus        = lazy(() => import('@pages/Admin/Menus/Menus'));
const Users        = lazy(() => import('@pages/Admin/Users/Users'));

// ── Suspense wrapper ───────────────────────────────────────────────────────
const withSuspense = (Component) => (
    <Suspense fallback={<PageLoader />}>
        <Component />
    </Suspense>
);

// ── Admin providers wrapper ────────────────────────────────────────────────
function AdminRoot({ children }) {
    return (
        <AdminAuthProvider>
            <ToastProvider>
                {children}
            </ToastProvider>
        </AdminAuthProvider>
    );
}

// ── Router configuration ───────────────────────────────────────────────────
export const router = createBrowserRouter([
    // ── Public marketing routes ──────────────────────────────────────────
    {
        element: <MainLayout />,
        children: [
            { index: true, element: withSuspense(Home) },
            { path: 'products', element: withSuspense(Products) },
            { path: 'services', element: withSuspense(Services) },
            { path: 'user-guide', element: withSuspense(UserGuide) },
            { path: 'features', element: withSuspense(Features) },
            { path: 'solutions', element: withSuspense(Solutions) },
        ],
    },
    {
        element: <BlogLayout />,
        children: [
            { path: 'blog', element: withSuspense(Blog) },
            { path: 'blog/:slug', element: withSuspense(BlogPost) },
        ],
    },

    // ── Admin panel routes ───────────────────────────────────────────────
    {
        path: 'adminpanel',
        element: <AdminRoot><Suspense fallback={<PageLoader />}><AdminLayout /></Suspense></AdminRoot>,
        children: [
            // redirect /adminpanel → /adminpanel/dashboard
            { index: true, element: <Navigate to="/adminpanel/dashboard" replace /> },
            // protected pages
            {
                element: <ProtectedAdminRoute />,
                children: [
                    { path: 'dashboard', element: withSuspense(Dashboard) },
                    { path: 'menus', element: withSuspense(Menus) },
                    { path: 'users', element: withSuspense(Users) },
                    { path: 'models/upload', element: withSuspense(UploadModel) },
                    { path: 'models/list', element: withSuspense(ModelsList) },
                ],
            },
        ],
    },
    // Public admin auth pages (outside AdminLayout but still inside AdminRoot)
    {
        path: 'adminpanel/login',
        element: <AdminRoot><Suspense fallback={<PageLoader />}><AdminLogin /></Suspense></AdminRoot>,
    },
    {
        path: 'adminpanel/signup',
        element: <AdminRoot><Suspense fallback={<PageLoader />}><AdminSignup /></Suspense></AdminRoot>,
    },

    // ── 404 ──────────────────────────────────────────────────────────────
    {
        path: '*',
        element: withSuspense(NotFound),
    },
]);
